import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export default function SettingsPage({ currentUser }) {
  const isAdmin = currentUser?.role === 'admin';

  const [activeTab, setActiveTab] = useState('suppliers');

  // Suppliers
  const [suppliers, setSuppliers]         = useState([]);
  const [suppLoading, setSuppLoading]     = useState(true);
  const [suppSaving, setSuppSaving]       = useState(false);
  const [newSupplier, setNewSupplier]     = useState({ name: '', type: 'external' });
  const [editingSupp, setEditingSupp]     = useState(null); // { id, name, type }

  // Categories
  const [categories, setCategories]       = useState([]);
  const [catLoading, setCatLoading]       = useState(true);
  const [catSaving, setCatSaving]         = useState(false);
  const [newCategory, setNewCategory]     = useState('');
  const [editingCat, setEditingCat]       = useState(null); // { id, name }

  useEffect(() => {
    supabase.from('suppliers').select('*').order('name')
      .then(({ data }) => { if (data) setSuppliers(data); setSuppLoading(false); });
    supabase.from('categories').select('*').order('name')
      .then(({ data }) => { if (data) setCategories(data); setCatLoading(false); });
  }, []);

  // ── SUPPLIERS ─────────────────────────────────────────────────────────

  async function handleAddSupplier() {
    if (!newSupplier.name.trim()) return;
    setSuppSaving(true);
    const { data, error } = await supabase
      .from('suppliers')
      .insert({ name: newSupplier.name.trim(), type: newSupplier.type, active: true })
      .select().single();
    if (!error && data) setSuppliers(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    setNewSupplier({ name: '', type: 'external' });
    setSuppSaving(false);
  }

  async function handleSaveSupplier() {
    if (!editingSupp?.name.trim()) return;
    setSuppSaving(true);
    const { error } = await supabase
      .from('suppliers')
      .update({ name: editingSupp.name.trim(), type: editingSupp.type })
      .eq('id', editingSupp.id);
    if (!error) {
      setSuppliers(prev => prev.map(s => s.id === editingSupp.id ? { ...s, name: editingSupp.name.trim(), type: editingSupp.type } : s).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingSupp(null);
    }
    setSuppSaving(false);
  }

  async function handleToggleSupplier(supplier) {
    const newActive = !supplier.active;
    const { error } = await supabase.from('suppliers').update({ active: newActive }).eq('id', supplier.id);
    if (!error) setSuppliers(prev => prev.map(s => s.id === supplier.id ? { ...s, active: newActive } : s));
  }

  // ── CATEGORIES ────────────────────────────────────────────────────────

  async function handleAddCategory() {
    if (!newCategory.trim()) return;
    setCatSaving(true);
    const { data, error } = await supabase
      .from('categories')
      .insert({ name: newCategory.trim(), active: true })
      .select().single();
    if (!error && data) setCategories(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    setNewCategory('');
    setCatSaving(false);
  }

  async function handleSaveCategory() {
    if (!editingCat?.name.trim()) return;
    setCatSaving(true);
    const { error } = await supabase
      .from('categories')
      .update({ name: editingCat.name.trim() })
      .eq('id', editingCat.id);
    if (!error) {
      setCategories(prev => prev.map(c => c.id === editingCat.id ? { ...c, name: editingCat.name.trim() } : c).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingCat(null);
    }
    setCatSaving(false);
  }

  async function handleToggleCategory(cat) {
    const newActive = !cat.active;
    const { error } = await supabase.from('categories').update({ active: newActive }).eq('id', cat.id);
    if (!error) setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, active: newActive } : c));
  }

  // ── STYLES ────────────────────────────────────────────────────────────

  const pill = (active) => ({
    padding: '0.35rem 0.9rem', borderRadius: '999px', border: '1px solid',
    borderColor: active ? '#1a1a1a' : '#ddd',
    background: active ? '#1a1a1a' : '#fff',
    color: active ? '#fff' : '#555',
    fontSize: '0.88rem', cursor: 'pointer', fontWeight: active ? 600 : 400,
  });

  const inputStyle = {
    padding: '0.45rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px',
    fontSize: '0.9rem', outline: 'none',
  };

  const btnPrimary = (disabled) => ({
    padding: '0.45rem 1.1rem', background: disabled ? '#ccc' : '#1a1a1a',
    color: '#fff', border: 'none', borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.88rem',
  });

  const btnSecondary = {
    padding: '0.45rem 0.9rem', background: '#fff', border: '1px solid #ddd',
    borderRadius: '6px', cursor: 'pointer', fontSize: '0.88rem',
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>
        Settings are only accessible to admins.
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '1.5rem' }}>Settings</h2>

      {/* TABS */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem' }}>
        <button onClick={() => setActiveTab('suppliers')} style={pill(activeTab === 'suppliers')}>Suppliers</button>
        <button onClick={() => setActiveTab('categories')} style={pill(activeTab === 'categories')}>Categories</button>
      </div>

      {/* ── SUPPLIERS TAB ── */}
      {activeTab === 'suppliers' && (
        <div>
          {/* Add new */}
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '1.25rem', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>Add Supplier</h3>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                value={newSupplier.name}
                onChange={e => setNewSupplier(p => ({ ...p, name: e.target.value }))}
                placeholder='Supplier name'
                style={{ ...inputStyle, minWidth: '200px', flex: 1 }}
                onKeyDown={e => e.key === 'Enter' && handleAddSupplier()}
              />
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  onClick={() => setNewSupplier(p => ({ ...p, type: 'external' }))}
                  style={{ ...pill(newSupplier.type === 'external'), fontSize: '0.82rem', padding: '0.3rem 0.75rem' }}>
                  External
                </button>
                <button
                  onClick={() => setNewSupplier(p => ({ ...p, type: 'internal' }))}
                  style={{ ...pill(newSupplier.type === 'internal'), fontSize: '0.82rem', padding: '0.3rem 0.75rem' }}>
                  Internal
                </button>
              </div>
              <button onClick={handleAddSupplier} disabled={suppSaving || !newSupplier.name.trim()} style={btnPrimary(suppSaving || !newSupplier.name.trim())}>
                {suppSaving ? 'Adding…' : 'Add'}
              </button>
            </div>
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.78rem', color: '#aaa' }}>
              Internal = Prep Kitchen Product (triggers internal transfer on stocktake). External = direct supplier (triggers external order reference).
            </p>
          </div>

          {/* List */}
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            {suppLoading ? (
              <p style={{ padding: '1.5rem', color: '#aaa' }}>Loading…</p>
            ) : suppliers.length === 0 ? (
              <p style={{ padding: '1.5rem', color: '#aaa' }}>No suppliers yet.</p>
            ) : suppliers.map((s, i) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', padding: '0.85rem 1.1rem', borderBottom: i < suppliers.length - 1 ? '1px solid #f0f0f0' : 'none', gap: '0.75rem', flexWrap: 'wrap', opacity: s.active ? 1 : 0.5 }}>

                {editingSupp?.id === s.id ? (
                  <>
                    <input
                      value={editingSupp.name}
                      onChange={e => setEditingSupp(p => ({ ...p, name: e.target.value }))}
                      style={{ ...inputStyle, flex: 1, minWidth: '150px' }}
                      onKeyDown={e => e.key === 'Enter' && handleSaveSupplier()}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => setEditingSupp(p => ({ ...p, type: 'external' }))} style={{ ...pill(editingSupp.type === 'external'), fontSize: '0.78rem', padding: '0.25rem 0.65rem' }}>External</button>
                      <button onClick={() => setEditingSupp(p => ({ ...p, type: 'internal' }))} style={{ ...pill(editingSupp.type === 'internal'), fontSize: '0.78rem', padding: '0.25rem 0.65rem' }}>Internal</button>
                    </div>
                    <button onClick={handleSaveSupplier} disabled={suppSaving} style={btnPrimary(suppSaving)}>Save</button>
                    <button onClick={() => setEditingSupp(null)} style={btnSecondary}>Cancel</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.92rem' }}>{s.name}</span>
                    </div>
                    <span style={{ background: s.type === 'internal' ? '#f0f5ff' : '#f5f5f5', color: s.type === 'internal' ? '#4a6fa5' : '#777', padding: '0.18rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 500 }}>
                      {s.type === 'internal' ? 'Internal' : 'External'}
                    </span>
                    <span style={{ fontSize: '0.78rem', color: s.active ? '#6abf69' : '#aaa', fontWeight: 600 }}>
                      {s.active ? 'Active' : 'Inactive'}
                    </span>
                    <button onClick={() => setEditingSupp({ id: s.id, name: s.name, type: s.type })} style={btnSecondary}>Edit</button>
                    <button onClick={() => handleToggleSupplier(s)}
                      style={{ ...btnSecondary, borderColor: s.active ? '#ffcccc' : '#ddd', color: s.active ? '#cc4444' : '#555' }}>
                      {s.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CATEGORIES TAB ── */}
      {activeTab === 'categories' && (
        <div>
          {/* Add new */}
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '1.25rem', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>Add Category</h3>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                placeholder='Category name'
                style={{ ...inputStyle, minWidth: '200px', flex: 1 }}
                onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
              />
              <button onClick={handleAddCategory} disabled={catSaving || !newCategory.trim()} style={btnPrimary(catSaving || !newCategory.trim())}>
                {catSaving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>

          {/* List */}
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            {catLoading ? (
              <p style={{ padding: '1.5rem', color: '#aaa' }}>Loading…</p>
            ) : categories.length === 0 ? (
              <p style={{ padding: '1.5rem', color: '#aaa' }}>No categories yet.</p>
            ) : categories.map((c, i) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', padding: '0.85rem 1.1rem', borderBottom: i < categories.length - 1 ? '1px solid #f0f0f0' : 'none', gap: '0.75rem', flexWrap: 'wrap', opacity: c.active ? 1 : 0.5 }}>

                {editingCat?.id === c.id ? (
                  <>
                    <input
                      value={editingCat.name}
                      onChange={e => setEditingCat(p => ({ ...p, name: e.target.value }))}
                      style={{ ...inputStyle, flex: 1, minWidth: '150px' }}
                      onKeyDown={e => e.key === 'Enter' && handleSaveCategory()}
                      autoFocus
                    />
                    <button onClick={handleSaveCategory} disabled={catSaving} style={btnPrimary(catSaving)}>Save</button>
                    <button onClick={() => setEditingCat(null)} style={btnSecondary}>Cancel</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.92rem' }}>{c.name}</span>
                    </div>
                    <span style={{ fontSize: '0.78rem', color: c.active ? '#6abf69' : '#aaa', fontWeight: 600 }}>
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                    <button onClick={() => setEditingCat({ id: c.id, name: c.name })} style={btnSecondary}>Edit</button>
                    <button onClick={() => handleToggleCategory(c)}
                      style={{ ...btnSecondary, borderColor: c.active ? '#ffcccc' : '#ddd', color: c.active ? '#cc4444' : '#555' }}>
                      {c.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}